<template>
  <div class="businesses">
    <n-card>
      <template #header>
        <div class="card-header">
          <span>All Businesses</span>
          <n-space>
            <n-input
              v-model:value="searchText"
              placeholder="Search businesses..."
              style="width: 300px"
              clearable
            />
            <n-button type="primary" @click="showCreateModal = true">
              + Create Business
            </n-button>
          </n-space>
        </div>
      </template>

      <n-data-table
        :columns="columns"
        :data="filteredBusinesses"
        :loading="loading"
        :row-key="(row) => row.id"
        :row-props="rowProps"
        :default-sort="{ columnKey: 'created_at', order: 'descend' }"
      />
    </n-card>

    <!-- Create Business Modal -->
    <n-modal v-model:show="showCreateModal" preset="dialog" title="Create New Business">
      <n-form ref="formRef" :model="formData" :rules="formRules" label-placement="left" label-width="120">
        <n-form-item label="Business Name" path="name">
          <n-input v-model:value="formData.name" placeholder="Enter business name" />
        </n-form-item>
        <n-form-item label="Phone" path="phone">
          <n-input v-model:value="formData.phone" placeholder="Enter 10 digit phone" maxlength="10" :allow-input="allowOnlyDigits" />
        </n-form-item>
        <n-form-item label="Email" path="email">
          <n-input v-model:value="formData.email" placeholder="Enter email" />
        </n-form-item>
        <n-form-item label="Tax Regime" path="tax_regime">
          <n-select
            v-model:value="formData.tax_regime"
            :options="taxRegimeOptions"
            placeholder="Select tax regime"
          />
        </n-form-item>
        <n-form-item v-if="formData.tax_regime === 'gst'" label="GSTIN" path="gstin">
          <n-input v-model:value="formData.gstin" placeholder="Enter GSTIN" />
        </n-form-item>
        <n-form-item label="State Code" path="state_code">
          <n-input v-model:value="formData.state_code" placeholder="e.g., TN, KA" />
        </n-form-item>
        <n-form-item label="Address" path="address">
          <n-input v-model:value="formData.address" type="textarea" placeholder="Enter address" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-space justify="end">
          <n-button @click="showCreateModal = false">Cancel</n-button>
          <n-button type="primary" :loading="submitting" @click="handleCreateBusiness">
            Create Business
          </n-button>
        </n-space>
      </template>
    </n-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import { useRouter } from 'vue-router'
import { NCard, NDataTable, NInput, NTag, NSpace, NButton, NModal, NForm, NFormItem, NSelect } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAdmin } from '../composables/useAdmin'
import { allowOnlyDigits, emailRules } from '@shared/utils/validators'
import dayjs from 'dayjs'

const router = useRouter()
const toast = useToast()
const { listBusinesses, createBusiness, loading } = useAdmin()

const businesses = ref([])
const searchText = ref('')
const showCreateModal = ref(false)
const submitting = ref(false)
const formRef = ref(null)

const formData = ref({
  name: '',
  phone: '',
  email: '',
  tax_regime: 'non_gst',
  gstin: '',
  state_code: '',
  address: '',
})

const taxRegimeOptions = [
  { label: 'Non-GST', value: 'non_gst' },
  { label: 'GST', value: 'gst' },
]

const formRules = {
  name: { required: true, message: 'Business name is required', trigger: 'blur' },
  phone: [
    { pattern: /^\d{10}$/, message: 'Enter 10 digit phone number', trigger: 'blur' }
  ],
  email: emailRules,
  tax_regime: { required: true, message: 'Tax regime is required', trigger: 'change' },
  gstin: {
    validator: (rule, value) => {
      if (formData.value.tax_regime === 'gst' && !value) {
        return new Error('GSTIN is required for GST businesses')
      }
      return true
    },
    trigger: 'blur',
  },
}

const filteredBusinesses = computed(() => {
  if (!searchText.value) return businesses.value
  const search = searchText.value.toLowerCase()
  return businesses.value.filter(
    (b) =>
      b.name.toLowerCase().includes(search) ||
      b.email?.toLowerCase().includes(search) ||
      b.phone?.includes(search)
  )
})

const columns = [
  { title: 'ID', key: 'id', width: 60, sorter: 'default' },
  { title: 'Business Name', key: 'name', sorter: 'default' },
  { title: 'Phone', key: 'phone' },
  { title: 'Email', key: 'email' },
  {
    title: 'Created',
    key: 'created_at',
    width: 120,
    sorter: 'default',
    render: (row) => dayjs(row.created_at).format('MMM DD, YYYY'),
  },
  {
    title: 'Licenses',
    key: 'licenses',
    render: (row) => {
      const licenses = row.licenses || []
      if (licenses.length === 0) {
        return h(NTag, { type: 'default', size: 'small' }, () => 'No licenses')
      }
      return h(NSpace, { size: 'small' }, () =>
        licenses.map((lic) => {
          let type = 'success'
          if (lic.is_expired) type = 'error'
          else if (lic.days_remaining <= 30) type = 'warning'
          return h(
            NTag,
            { type, size: 'small', key: lic.id },
            () => `${lic.module_name} (${lic.is_expired ? 'expired' : lic.days_remaining + 'd'})`
          )
        })
      )
    },
  },
]

const rowProps = (row) => {
  return {
    style: 'cursor: pointer;',
    onClick: () => {
      router.push(`/businesses/${row.id}`)
    }
  }
}

const handleCreateBusiness = async () => {
  try {
    await formRef.value?.validate()
  } catch {
    return
  }

  submitting.value = true
  try {
    const newBusiness = await createBusiness(formData.value)
    toast.success('Business created successfully')
    showCreateModal.value = false

    // Reset form
    formData.value = {
      name: '',
      phone: '',
      email: '',
      tax_regime: 'non_gst',
      gstin: '',
      state_code: '',
      address: '',
    }

    // Navigate to new business detail page
    router.push(`/businesses/${newBusiness.id}`)
  } catch (error) {
    toast.error(error.message || 'Failed to create business')
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  try {
    const response = await listBusinesses()
    businesses.value = response.businesses || []
  } catch (error) {
    console.error('Failed to load businesses:', error)
  }
})
</script>

<style scoped>
.businesses {
  max-width: 1200px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
